// Predicted strength and sugar of a development recipe (Joe, 21 Oct 2026).
//
// A stage (or a single-stage recipe) is written per N litres of what it
// makes, so its strength is the pure alcohol in its ingredients over N, and
// its sugar is the sugar in them over N. Spirits' strengths come from the
// stock item's ABV, else a % in its name ("Neutral Grain Spirit 96%",
// "GNS 96"); an earlier stage's output carries that stage's strength and
// sugar. Sugars count in full; common syrups at a typical sugar content,
// said so on screen. Density and hydrometer reading follow (OIML R22, sugar
// at 0.625 L/kg).

import { rho, pFromAbv, liqueurDensity, apparentAbv } from './oiml.js';

const SPIRIT_RE = /\b(spirits?|neutral|gns|ngs|vodka|gin|rum|whisk(e)?y|brandy|grappa|eau de vie|cognac|tequila|mezcal|liqueur|wine|sherry|port|vermouth|distillate|ethanol|alcohol)\b/i;
const SUGAR_RE = /\b(sugar|sucrose|fructose|dextrose|glucose|caster|demerara|muscovado)\b/i;
// Syrups: sugar as a fraction of the syrup's weight, and its density (kg/L).
const SYRUPS = [
  [/agave/i, 0.68, 1.33, 'agave syrup ≈ 68% sugar'],
  [/honey/i, 0.80, 1.42, 'honey ≈ 80% sugar'],
  [/maple/i, 0.66, 1.32, 'maple syrup ≈ 66% sugar'],
  [/\b(sugar|simple|gomme|stock)\s+syrup\b/i, 0.5, 1.23, 'sugar syrup taken as 1:1 (50% sugar)'],
  [/\b(invert|glucose)\s+syrup\b/i, 0.75, 1.36, 'invert/glucose syrup ≈ 75% sugars'],
];
const WATER_RE = /\bwater\b/i;

const nameOf = (g) => `${g.item_name || ''} ${g.name_as_written || ''}`;

/** A spirit's strength: stock item ABV, else a % in its name. */
export function abvOfLine(g) {
  if (Number(g.item_abv) > 0) return Number(g.item_abv);
  const n = nameOf(g);
  const pct = n.match(/(\d{2,3}(?:\.\d+)?)\s*%/);
  if (pct && Number(pct[1]) > 0 && Number(pct[1]) <= 100) return Number(pct[1]);
  const gns = n.match(/\b(?:gns|ngs|neutral(?: grain)?(?: spirit)?)\s*(\d{2}(?:\.\d+)?)\b/i);
  if (gns && Number(gns[1]) <= 100) return Number(gns[1]);
  return null;
}

function litres(g, abv) {
  const a = Number(g.amount); const u = String(g.unit || '').toLowerCase();
  if (!Number.isFinite(a)) return null;
  if (u === 'l' || u === 'litres' || u === 'litre') return a;
  if (u === 'ml') return a / 1000;
  if (u === 'cl') return a / 100;
  const kg = u === 'kg' ? a : u === 'g' ? a / 1000 : null;
  if (kg == null) return null;
  if (Number(g.item_density) > 0) return kg / Number(g.item_density); // the material's own density
  if (abv != null) return kg / (rho(pFromAbv(abv), 20) / 1000);
  if (WATER_RE.test(nameOf(g))) return kg / 0.9982;
  return null;
}

function massKg(g) {
  const a = Number(g.amount); const u = String(g.unit || '').toLowerCase();
  if (!Number.isFinite(a)) return null;
  return u === 'kg' ? a : u === 'g' ? a / 1000 : u === 'mg' ? a / 1e6 : null;
}

/**
 * One stage: lines per `basis` litres. `from` holds earlier stages'
 * { abv, sugar } (g/L) for lines that are a stage's output.
 */
export function stageStrength(lines, basis, from = {}) {
  const B = Number(basis) || 1;
  let lpa = 0; let sugarKg = 0; let solidsL = 0; // solidsL: litres the dissolved sugar/solids take up
  const spirits = []; const unknownSpirits = []; const sugars = []; const unknownSugars = []; const assumptions = [];
  for (const g of lines) {
    if (g.amount == null) continue;
    if (g.from_stage) {
      const s = from[g.from_stage];
      const L = litres(g, s?.abv ?? null) ?? (String(g.unit).toLowerCase() === 'l' ? Number(g.amount) : null);
      if (s && L != null) {
        if (s.abv != null) { lpa += (L * s.abv) / 100; spirits.push({ name: `Stage ${g.from_stage} output`, litres: L, abv: s.abv }); }
        if (s.sugar) { sugarKg += (L * s.sugar) / 1000; solidsL += ((L * s.sugar) / 1000) * (s.k || 0.625); }
      } else unknownSpirits.push(`Stage ${g.from_stage} output`);
      continue;
    }
    const n = nameOf(g);
    const name = g.item_name || g.name_as_written;
    // The material's own composition (Stock → item → Composition), when set.
    if (g.item_sugar_pct != null && g.item_sugar_pct !== '') {
      const pct = Number(g.item_sugar_pct) / 100;
      const volUnit = ['l', 'ml', 'cl', 'litres', 'litre'].includes(String(g.unit).toLowerCase());
      const kg = massKg(g) ?? (volUnit && Number(g.item_density) > 0 ? litres(g, null) * Number(g.item_density) : null);
      if (kg != null) {
        if (pct > 0) { sugarKg += kg * pct; solidsL += kg * pct * (Number(g.item_solids_l_kg) || 0.63); sugars.push({ name, kg: kg * pct }); }
      } else unknownSugars.push(`${name} (set its density to convert litres to kg)`);
      // It may carry alcohol as well (a liqueur): count that below; skip the name guesses.
      const abvS = abvOfLine(g);
      if (abvS != null) {
        const L = litres(g, abvS);
        if (L != null) { lpa += (L * abvS) / 100; spirits.push({ name, litres: L, abv: abvS }); }
      }
      continue;
    }
    const syrup = SYRUPS.find(([re]) => re.test(n));
    if (syrup) {
      const [, frac, dens, note] = syrup;
      const volL = ['l', 'ml', 'cl', 'litres', 'litre'].includes(String(g.unit).toLowerCase()) ? litres(g, null) : null;
      const syrupKg = massKg(g) ?? (volL != null ? volL * dens : null);
      if (syrupKg != null) { sugarKg += syrupKg * frac; solidsL += syrupKg * frac * 0.62; sugars.push({ name: g.item_name || g.name_as_written, kg: syrupKg * frac }); assumptions.push(note); } else unknownSugars.push(g.item_name || g.name_as_written);
      continue;
    }
    if (SUGAR_RE.test(n) && !/syrup/i.test(n)) {
      const kg = massKg(g);
      if (kg != null) { sugarKg += kg; solidsL += kg * 0.63; sugars.push({ name: g.item_name || g.name_as_written, kg }); } else unknownSugars.push(g.item_name || g.name_as_written);
      continue;
    }
    if (/syrup/i.test(n)) { unknownSugars.push(g.item_name || g.name_as_written); continue; }
    const abv = abvOfLine(g);
    if (abv != null) {
      const L = litres(g, abv);
      if (L != null) { lpa += (L * abv) / 100; spirits.push({ name: g.item_name || g.name_as_written, litres: L, abv }); } else unknownSpirits.push(g.item_name || g.name_as_written);
    } else if (SPIRIT_RE.test(n) && !WATER_RE.test(n)) unknownSpirits.push(g.item_name || g.name_as_written);
  }
  const abv = (lpa / B) * 100;
  const sugar = (sugarKg * 1000) / B; // g/L
  const k = sugarKg > 0 ? solidsL / sugarKg : 0.625; // the mix's dissolved volume per kg
  const density = abv <= 100 ? liqueurDensity(Math.min(abv, 99.9), sugar, k) : NaN;
  const apparent = sugar > 0 ? apparentAbv(Math.min(abv, 99.9), sugar, k) : abv;
  return { basis: B, lpa, abv, sugar, k, density, apparent, spirits, sugars, unknownSpirits, unknownSugars, assumptions: [...new Set(assumptions)] };
}

/** Every stage of a staged iteration, in order, each feeding the next. */
export function versionStrength(v) {
  const out = {};
  const from = {};
  for (const st of [...(v.stages || [])].sort((a, b) => a.stage_number - b.stage_number)) {
    const s = stageStrength(v.ingredients.filter((g) => g.stage_number === st.stage_number), st.basis_litres, from);
    out[st.stage_number] = s;
    // What later stages get from this one: its predicted strength (or its target if nothing counted) and sugar.
    from[st.stage_number] = { abv: s.lpa > 0 ? s.abv : (st.target_abv != null ? Number(st.target_abv) : null), sugar: s.sugar, k: s.k };
  }
  return out;
}
